
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';

// Extend Bill type to include stage/SLA fields added by migration (optional to avoid breaking existing UI)
export type BillStage =
  | 'intake'
  | 'extraction'
  | 'processing'
  | 'manual_review'
  | 'validation'
  | 'approval'
  | 'payment_ready'
  | 'archive';

export interface Bill {
  id: string;
  user_id: string;
  account_id: string;
  bill_number?: string;
  bill_date: string;
  due_date?: string;
  amount: number;
  currency: string;
  usage_amount?: number;
  usage_unit?: string;
  billing_period_start?: string;
  billing_period_end?: string;
  supplier_id?: string;
  supplier_confidence?: number;
  supplier_detection_method?: string;
  pdf_url?: string;
  raw_data?: any;
  parsed_data?: any;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed' | 'review_required';
  ai_confidence?: number;
  anomalies?: any;
  collection_status: 'pending' | 'collected' | 'failed' | 'manual';
  created_at: string;
  updated_at: string;

  // New optional fields for 8-stage workflow & SLA
  stage?: BillStage;
  stage_entered_at?: string | null;
  sla_due_at?: string | null;
  stage_breach?: boolean;
  breach_reason?: string | null;
  csv_url?: string | null;
  site_id?: string | null;
  meter_id?: string | null;
  retailer_id?: string | null;
  sla_policy_id?: string | null;
  
  // Assignment tracking fields
  assigned_to?: string | null;
  assigned_at?: string | null;
  claimed_by?: string | null;
  claimed_at?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_duration_seconds?: number | null;
}

export interface CreateBillData {
  account_id: string;
  bill_number?: string;
  bill_date: string;
  due_date?: string;
  amount: number;
  currency?: string;
  usage_amount?: number;
  usage_unit?: string;
  billing_period_start?: string;
  billing_period_end?: string;
  pdf_url?: string;
  raw_data?: any;
  parsed_data?: any;
  processing_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'review_required';
  ai_confidence?: number;
  anomalies?: any;
  collection_status?: 'pending' | 'collected' | 'failed' | 'manual';

  // Optional stage fields upon creation
  stage?: BillStage;
}

export interface UpdateBillData extends Partial<CreateBillData> {
  id: string;
}

export const useBills = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  const billsQuery = useQuery({
    queryKey: ['bills', currentWorkspace?.id],
    queryFn: async () => {
      if (!user?.id || !currentWorkspace?.id) throw new Error('User or workspace not available');
      
      const { data, error } = await supabase
        .from('bills')
        .select(`
          *,
          accounts (
            account_number,
            service_type,
            sites (
              name,
              customers (
                name
              )
            ),
            partners (
              name
            )
          )
        `)
        .eq('workspace_id', currentWorkspace.id)
        .order('bill_date', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!currentWorkspace?.id,
    retry: 0
  });

  const createBillMutation = useMutation({
    mutationFn: async (newBill: CreateBillData) => {
      if (!user?.id) throw new Error('User not authenticated');

      const billData = {
        ...newBill,
        user_id: user.id,
        workspace_id: currentWorkspace.id,
        currency: newBill.currency || 'USD',
        collection_status: newBill.collection_status || 'collected',
        processing_status: newBill.processing_status || 'pending',
        stage: newBill.stage || 'intake',
      };

      const { data, error } = await supabase
        .from('bills')
        .insert(billData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast({
        title: "Success",
        description: "Bill created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create bill",
        variant: "destructive",
      });
    },
  });

  const updateBillMutation = useMutation({
    mutationFn: async ({ id, ...updates }: UpdateBillData) => {
      if (!user?.id) throw new Error('User not authenticated');
      const { data, error } = await supabase
        .from('bills')
        .update(updates)
        .eq('id', id)
        .eq('workspace_id', currentWorkspace.id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Bill not found');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast({
        title: "Success",
        description: "Bill updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update bill",
        variant: "destructive",
      });
    },
  });

  const deleteBillMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      const { error } = await supabase
        .from('bills')
        .delete()
        .eq('id', id)
        .eq('workspace_id', currentWorkspace.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast({
        title: "Success",
        description: "Bill deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete bill",
        variant: "destructive",
      });
    },
  });

  // Statistics queries
  const billStatsQuery = useQuery({
    queryKey: ['bill-stats', currentWorkspace?.id],
    queryFn: async () => {
      if (!user?.id || !currentWorkspace?.id) throw new Error('User or workspace not available');
      
      const { data, error } = await supabase
        .from('bills')
        .select('amount, processing_status')
        .eq('workspace_id', currentWorkspace.id);

      if (error) throw error;

      const totalBills = data.length;
      const totalAmount = data.reduce((sum, bill) => sum + Number(bill.amount), 0);
      const processedBills = data.filter(bill => bill.processing_status === 'completed').length;
      const reviewBills = data.filter(bill => bill.processing_status === 'review_required').length;

      return {
        totalBills,
        totalAmount,
        processedBills,
        reviewBills,
      };
    },
    enabled: !!user?.id && !!currentWorkspace?.id,
    retry: 0
  });

  return {
    bills: billsQuery.data || [],
    billStats: billStatsQuery.data,
    isLoading: billsQuery.isLoading,
    error: billsQuery.error,
    createBill: createBillMutation.mutateAsync,
    updateBill: updateBillMutation.mutateAsync,
    deleteBill: deleteBillMutation.mutate,
    isCreating: createBillMutation.isPending,
    isUpdating: updateBillMutation.isPending,
    isDeleting: deleteBillMutation.isPending,
  };
};
